import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formSchema, type FormSchema } from "./onboarding-schema";
import { tabSync } from "@/utils/tabSync";

export const useOnboardingForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: "",
      primary_focus: [],
      other_focus: "",
      discovery_source: "",
      other_discovery: "",
    },
  });

  const showOtherFocus = form.watch("primary_focus")?.includes("other");
  const showOtherDiscovery = form.watch("discovery_source") === "other";

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (step === 1) {
      const nameValue = form.getValues("full_name");
      if (!nameValue || nameValue.length < 2) {
        form.setError("full_name", {
          type: "manual",
          message: "Name must be at least 2 characters",
        });
        return;
      }
    }
    
    if (step === 2) {
      const focusValues = form.getValues("primary_focus");
      if (!focusValues || focusValues.length === 0) {
        form.setError("primary_focus", {
          type: "manual",
          message: "Select at least one focus area",
        });
        return;
      }
    }
    
    nextStep();
  };

  const onSubmit = async (values: FormSchema) => {
    try {
      setIsSubmitting(true);
      console.log("Starting form submission...");
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error("Error getting user:", userError);
        throw new Error("No user found");
      }

      console.log("Got user:", user.id);
      console.log("Submitting values:", values);

      // Insert into both onboarding_responses and onboarding tables
      const [onboardingError, responseError] = await Promise.all([
        // New onboarding table
        supabase
          .from('onboarding')
          .upsert({
            id: user.id,
            name: values.full_name,
            focus: values.primary_focus.join(','),
            discovery: [values.discovery_source, ...(showOtherDiscovery ? [values.other_discovery] : [])]
          }),
        // Existing onboarding_responses table
        supabase
          .from('onboarding_responses')
          .upsert({
            user_id: user.id,
            full_name: values.full_name,
            primary_focus: values.primary_focus,
            other_focus: showOtherFocus ? values.other_focus : null,
            discovery_source: values.discovery_source,
            other_discovery: showOtherDiscovery ? values.other_discovery : null,
          })
      ]).then(([onboardingRes, responseRes]) => [onboardingRes.error, responseRes.error]);

      if (onboardingError || responseError) {
        console.error("Error saving responses:", { onboardingError, responseError });
        throw onboardingError || responseError;
      }

      // Initialize premium features for the user
      const { error: premiumError } = await supabase
        .from('premium_features')
        .upsert({
          id: user.id,
          has_advanced_analytics: false,
          custom_themes: false,
          priority_support: false
        });

      if (premiumError) {
        console.error("Error initializing premium features:", premiumError);
        // Don't throw here as this is not critical for onboarding
      }

      console.log("Form submitted successfully, notifying other tabs...");
      
      // Mark onboarding as completed in localStorage to prevent showing it again
      localStorage.setItem('onboarding_completed', 'true');
      
      // Notify other tabs about onboarding completion
      tabSync.notifyOnboardingComplete();
      
      toast({
        title: "Welcome to Strykr.ai! 🎉",
        description: "Thank you for completing the onboarding survey.",
      });

      // Show completion screen instead of immediately navigating
      setIsCompleted(true);
      
      // Navigate after short delay to allow viewing completion screen
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 3000);
    } catch (error) {
      console.error("Error submitting survey:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit survey. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    step,
    isSubmitting,
    isCompleted,
    showOtherFocus,
    showOtherDiscovery,
    handleNext,
    prevStep,
    onSubmit,
  };
};
